import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { isFieldMissingInEntry } from "@/lib/field-util.ts";
import type { FieldDef } from "@/lib/field-util.ts";
import { coupleLanguagePatch } from "@/lib/language-options.ts";
import { AddPlaceholder } from "./AddPlaceholder";
import { Button } from "./Button";
import { EntryFields } from "./EntryFields";
import type { EntryFieldValues } from "./EntryFields";
import { PlusButton } from "./PlusButton";
import { Switch } from "./Switch";
import { logApplicationFailure } from "@/lib/user-error";

// Composite "add an entry" affordance: a click-to-expand trigger + an
// inline form panel. The behavioral counterpart to the visual primitives
// <AddPlaceholder> and <PlusButton>.
//
// Two trigger variants:
//   variant='empty' — full-width outlined placeholder (via
//     <AddPlaceholder fullWidth>). For sections with no entries yet.
//   variant='row'   — slim text "+ add another" (via <PlusButton>) for
//     sections that already have entries.
//
// One input mode — an inline multi-field form driven by `fields`. The
// payload always ships as an object with the form values plus the
// share-time visibility from the embedded Switch. With `shareControl`
// off the switch is not rendered and `sharedPublic` ships as true: the
// caller governs visibility elsewhere (the profile's entry sections do
// it with one master per box) and overrides the flag itself.

type Variant = "empty" | "row";

export type AddFormPayload = EntryFieldValues & { sharedPublic: boolean };

interface AddFormProps {
  onAdd: (payload: AddFormPayload) => void | Promise<void>;
  fields: readonly FieldDef[];
  label?: string;
  variant?: Variant;
  /** Open the add form immediately on mount (deep-link from a quick action). */
  initiallyOpen?: boolean;
  /**
   * Anker-Präfix des Formulars. VOM AUFRUFER: das Profil zeigt mehrere
   * Abschnitte, und jeder kann sein Anlege-Formular gleichzeitig offen haben —
   * eine feste Kennung gäbe es dann mehrfach.
   */
  id?: string;
  /** Render the share switch (default). Off when the caller decides visibility for the box. */
  shareControl?: boolean;
}

export function AddForm({
  onAdd,
  fields,
  label,
  variant = "empty",
  initiallyOpen = false,
  id,
  shareControl = true,
}: AddFormProps) {
  const intl = useIntl();
  const labelText = label ?? intl.formatMessage({ id: "design.addEntry", defaultMessage: "Add entry" });
  const [adding, setAdding] = useState(initiallyOpen);
  const formRef = useRef<HTMLDivElement>(null);

  // Deep-linked open (e.g. /save/basics?add=experience): bring the freshly
  // opened form into view so the user lands right on it.
  useEffect(() => {
    if (initiallyOpen) formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [initiallyOpen]);
  const [form, setForm] = useState<EntryFieldValues>({});
  const [share, setShare] = useState(true);
  const [busy, setBusy] = useState(false);
  // Labels of required fields still empty on the last submit attempt - drives
  // the "missing fields" tooltip. Cleared as soon as the form changes.
  const [missing, setMissing] = useState<string[]>([]);

  const reset = () => {
    setAdding(false);
    setForm({});
    setShare(true);
    setBusy(false);
    setMissing([]);
  };

  async function confirm() {
    if (busy) return;
    const missingFields = fields.filter((f) => isFieldMissingInEntry(f, form));
    if (missingFields.length > 0) {
      setMissing(missingFields.map((f) => f.label));
      return;
    }
    setMissing([]);
    try {
      setBusy(true);
      await onAdd({ ...form, sharedPublic: share });
      reset();
    } catch {
      logApplicationFailure("application.add-entry");
      setBusy(false);
    }
  }

  if (adding) {
    return (
      <div ref={formRef} className="space-y-3 rounded-md bg-page p-4 ring-1 ring-ink/10">
        {shareControl ? (
          <div className="flex justify-end">
            <Switch
              id={id ? `${id}-share` : undefined}
              checked={share}
              onChange={setShare}
              ariaLabel={intl.formatMessage(
                share
                  ? { id: "common.share.ariaActive", defaultMessage: "Sharing active" }
                  : { id: "common.share.ariaInactive", defaultMessage: "Not sharing" },
              )}
              label={{
                on: intl.formatMessage({ id: "common.share.on", defaultMessage: "Share" }),
                off: intl.formatMessage({ id: "common.share.off", defaultMessage: "Private" }),
              }}
            />
          </div>
        ) : null}
        <EntryFields
          fields={fields}
          values={form}
          onChange={(key, val) => {
            setForm((s) => coupleLanguagePatch({ ...s, [key]: val }));
            setMissing([]);
          }}
        />
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            id={id && `${id}-cancel`}
            variant="secondary"
            onClick={reset}
            disabled={busy}
          >
            {intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" })}
          </Button>
          <div className="relative">
            {missing.length > 0 ? (
              <div
                id={id && `${id}-missing`}
                role="tooltip"
                className="absolute bottom-full right-0 mb-2 w-max max-w-[240px] rounded-md bg-page px-3 py-2 text-left text-smycles-label leading-snug text-ink-accent shadow-lg ring-1 ring-accent-decoration/40"
              >
                {intl.formatMessage({ id: "design.stillFillIn", defaultMessage: "Please still fill in: {fields}" }, { fields: missing.join(", ") })}
              </div>
            ) : null}
            <Button
              id={id && `${id}-confirm`}
              variant="primary"
              onClick={() => void confirm()}
              disabled={busy}
            >
              {busy ? "…" : intl.formatMessage({ id: "common.add", defaultMessage: "Add" })}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "row") {
    return <PlusButton id={id} label={labelText} onClick={() => setAdding(true)} className="mt-2" />;
  }

  return (
    <AddPlaceholder
      id={id}
      label={labelText}
      onClick={() => setAdding(true)}
      fullWidth
    />
  );
}
